import { registerSW } from 'virtual:pwa-register';

export const PWA_UPDATE_AVAILABLE_EVENT =
  'flower-blend:pwa-update-available' as const;

export type PwaUpdateCheckResult =
  | 'update-available'
  | 'up-to-date'
  | 'offline'
  | 'unsupported'
  | 'failed';

let applyWaitingUpdate: ((reloadPage?: boolean) => Promise<void>) | null = null;
let currentRegistration: ServiceWorkerRegistration | undefined;
let updateAvailable = false;
const updateListeners = new Set<() => void>();

function notifyUpdateAvailable(): void {
  updateAvailable = true;
  updateListeners.forEach((listener) => listener());
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PWA_UPDATE_AVAILABLE_EVENT));
  }
}

export function subscribePwaUpdate(listener: () => void): () => void {
  updateListeners.add(listener);
  return () => updateListeners.delete(listener);
}

export function getPwaUpdateSnapshot(): boolean {
  return updateAvailable;
}

export function hasWaitingPwaUpdate(): boolean {
  return Boolean(currentRegistration?.waiting);
}

function inspectWaitingWorker(registration?: ServiceWorkerRegistration): boolean {
  const candidate = registration ?? currentRegistration;
  if (!candidate?.waiting) return false;
  currentRegistration = candidate;
  notifyUpdateAvailable();
  return true;
}

/** Check once at a lifecycle boundary; never poll and never fail loudly offline. */
export async function requestPwaUpdateCheck(): Promise<PwaUpdateCheckResult> {
  if (typeof navigator === 'undefined' || !navigator.onLine) return 'offline';
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) {
    return 'unsupported';
  }
  try {
    const registration =
      currentRegistration ?? (await navigator.serviceWorker.getRegistration());
    currentRegistration = registration;
    if (inspectWaitingWorker(registration)) return 'update-available';
    await registration?.update();
    return inspectWaitingWorker(registration) ? 'update-available' : 'up-to-date';
  } catch {
    return 'failed';
  }
}

export async function checkForPwaUpdate(): Promise<boolean> {
  return (await requestPwaUpdateCheck()) === 'update-available';
}

export function dismissPwaUpdate(): void {
  // Keep the worker waiting; a later startup/foreground check can surface it.
  updateAvailable = false;
  updateListeners.forEach((listener) => listener());
}

/**
 * Register the production service worker without enabling it during Vite dev.
 * The managed update notice listens for PWA_UPDATE_AVAILABLE_EVENT and calls
 * applyPwaUpdate only after the user chooses to refresh.
 */
export function registerPwa(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;

  applyWaitingUpdate = registerSW({
    immediate: true,
    onNeedRefresh() {
      notifyUpdateAvailable();
    },
    onRegisteredSW(_scriptUrl, registration) {
      currentRegistration = registration;
      inspectWaitingWorker(registration);
    },
    onRegisterError(error) {
      console.error('PWA service-worker registration failed.', error);
    },
  });
}

export async function applyPwaUpdate(): Promise<void> {
  if (!applyWaitingUpdate) return;
  await applyWaitingUpdate(true);
}
